import * as React from 'react'
// We need to import this directly as the other hooks within the hooks folder
// are not SSR safe and causes the server to bundle these in.
import { useCookieConsentDecision } from 'hooks/useCookieConsentDecision'
import type {
  Cookie,
  CookiesPolicyProps,
  CookieSettingsLink
} from './CookiePolicy.types'

const storeTrackReason = 'Pour stocker et suivre les visites sur les sites web.'

const cookieTypes = {
  marketing: 'Marketing/ suivi',
  functional: 'Fonctionnel',
  analytical: 'Analytique',
  statistics: 'Statistiques'
}

const cookies: Cookie[] = [
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Bing Ads _uetvid',
    type: cookieTypes.marketing,
    duration: '1 an',
    purpose: storeTrackReason
  },
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Clarity _clsk',
    type: cookieTypes.marketing,
    duration: '1 jour',
    purpose:
      'Pour stocker et combiner les pages vues par un utilisateur en un seul enregistrement de session.'
  },
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Bing Ads _uetsid',
    type: cookieTypes.marketing,
    duration: '1 jour',
    purpose: storeTrackReason
  },
  {
    company: 'Laced',
    cookieName: 'rails_laced_app_session',
    type: cookieTypes.functional,
    duration: 'Session',
    purpose:
      'Pour stocker et suivre les visites sur les sites web. S’agit-il d’un appareil mobile, d’un numéro de panier, d’une identité d’invité, d’une identité de session, d’une identité de recherche ?'
  },
  {
    company: 'Laced',
    cookieName: '_laced_as',
    type: cookieTypes.analytical,
    duration: 'Session',
    purpose:
      'Pour identifier les recherches de produits effectuées au cours de la même session. Utilisé pour améliorer l’ordre des résultats de recherche.'
  },
  {
    company: 'Laced',
    cookieName: '_laced_asqp',
    type: cookieTypes.analytical,
    duration: 'Session',
    purpose:
      'Pour suivre la position des produits sélectionnés sur la page des résultats de la recherche. Utilisé pour améliorer l’ordre des résultats de recherche.'
  },
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Clarity _clck',
    type: cookieTypes.marketing,
    duration: '1 an',
    purpose: 'Pour stocker l’identifiant unique de l’utilisateur.'
  },
  {
    company: 'Google',
    cookieName: 'Google Analytics _gid',
    type: cookieTypes.analytical,
    duration: '1 jour',
    purpose: 'Pour stocker et compter les pages vues.'
  },
  {
    company: 'Stripe',
    cookieName: '__stripe_mid',
    type: cookieTypes.functional,
    duration: '1 an',
    purpose: 'Pour assurer la prévention des fraudes.'
  },
  {
    company: 'facebook',
    cookieName: '_fbp',
    type: cookieTypes.marketing,
    duration: '3 mois',
    purpose: storeTrackReason
  },
  {
    company: 'Google',
    cookieName: 'Google Analytics _ga',
    type: cookieTypes.statistics,
    duration: '1 an',
    purpose:
      'Ce cookie est un cookie persistant de Google Analytics qui est utilisé pour distinguer les utilisateurs uniques.'
  },
  {
    company: 'Google',
    cookieName: 'Google Adsense  _gcl_au',
    type: cookieTypes.marketing,
    duration: '1 an',
    purpose: 'Pour stocker et suivre les conversions.'
  },
  {
    company: 'Laced',
    cookieName: '',
    type: '',
    duration: '',
    purpose: 'Cookie de préférence.'
  }
]

const cookieSettingsLinks: CookieSettingsLink[] = [
  {
    label: 'Pour Microsoft Edge',
    href: 'https://support.microsoft.com/fr-fr/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09'
  },
  {
    label: 'Pour Chrome',
    href: 'https://support.google.com/chrome/answer/95647?hl=fr'
  },
  {
    label: 'Pour Firefox',
    href: 'https://support.mozilla.org/fr-FR/kb/delete-cookies-remove-info-websites-stored'
  },
  {
    label: 'Pour Safari',
    href: 'https://support.apple.com/fr-fr/guide/safari/sfri11471/mac'
  }
]

const CookiesPolicyFr: React.FC<CookiesPolicyProps> = ({
  siteHostname,
  stylizedHostname
}) => {
  const { mutate, existingCookiePreferences: preferences } =
    useCookieConsentDecision()

  return (
    <div className="layout legal">
      <h1>Politique en matière de cookies</h1>
      <p>
        Notre site web,{' '}
        <a
          href={`https://${siteHostname}/fr`}
          target="_blank"
          rel="noreferrer noopener"
        >
          {stylizedHostname}/fr
        </a>{' '}
        (notre &quot;site&quot;) utilise des cookies pour vous distinguer des
        autres utilisateurs de notre site. Cela nous aide à vous offrir une
        bonne expérience lorsque vous naviguez sur notre Site et nous permet
        également de l’améliorer. En continuant à naviguer sur notre Site, vous
        acceptez notre utilisation des cookies.
      </p>

      <h4>Qu’est-ce qu’un cookie?</h4>
      <p>
        Un cookie est un petit fichier de lettres et de chiffres que nous
        stockons sur votre navigateur ou sur le disque dur de votre ordinateur
        si vous y consentez. Les cookies peuvent être présents uniquement
        pendant la durée de votre visite sur notre Site ou ils peuvent être
        persistants pendant la durée de votre utilisation d’Internet ou pour une
        durée plus longue lorsqu’ils sont liés à la fourniture d’une expérience
        de navigation ou d’un service personnalisé pour vous sur notre site.
      </p>

      <h4>Quels sont les cookies que nous utilisons?</h4>
      <p>
        Nous utilisons les cookies suivants sur notre site avec les finalités
        ci-dessous:
      </p>
      <div className="legal-table-wrapper">
        <table className="legal-table caption">
          <thead>
            <tr>
              <td>Société</td>
              <td>Nom du cookie </td>
              <td>Type</td>
              <td>Durée</td>
              <td>Objectif</td>
            </tr>
          </thead>
          <tbody>
            {cookies.map((cookie) => (
              <tr key={cookie.cookieName}>
                <td>{cookie.company}</td>
                <td>{cookie.cookieName}</td>
                <td>{cookie.type}</td>
                <td>{cookie.duration}</td>
                <td>{cookie.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Vous pouvez à tout moment modifier vos{' '}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          href=""
          onClick={(e) => {
            e.preventDefault()
            mutate({ ...preferences, valid: false }, { revalidate: false })
          }}
        >
          préférences en matière de cookies
        </a>{' '}
        sur notre site.
      </p>
      <p>
        La plupart des logiciels de navigation Internet vous permettent de
        contrôler l’acceptation et le stockage des cookies. Vous pouvez bloquer
        les cookies en activant le paramètre du menu de votre navigateur qui
        vous permet de refuser le paramétrage de tous les cookies ou de certains
        d’entre eux.
      </p>
      <p>
        Pour modifier vos paramètres de cookies dans votre navigateur web,
        veuillez utiliser les liens suivants :
      </p>
      <ul className="legal-disc-list">
        {cookieSettingsLinks.map((link) => (
          <li key={link.href}>
            <a href={link.href} target="_blank" rel="noreferrer noopener">
              {link.label}
            </a>
          </li>
        ))}
      </ul>
      <p>
        Pour plus d’informations sur les cookies, leur contrôle ou leur gestion,
        veuillez consulter les{' '}
        <a
          href="https://www.cnil.fr/fr/home/"
          target="_blank"
          rel="noreferrer noopener"
        >
          conseils de l’Information Commissioner’s Office (bureau du commissaire
          à l’Information)
        </a>
        .
      </p>
      <p className="legal-timestamp footnote dark">
        Cette politique en matière de cookies a été mise à jour pour la dernière
        fois en juin 2023.
      </p>
    </div>
  )
}

export default CookiesPolicyFr
